/**
 * PM bonuses — TRBUD-459. The reads and writes behind the credit card, the
 * completion prompt, the rate screen and the Reports panel.
 *
 * Kept in their own file rather than in `queries.ts`: they are one feature's
 * hooks and share one key family, so every write invalidates the whole family
 * and a credit saved from the work order is on the Reports panel the next
 * time it is read.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  Envelope,
  PmBonusRatesResponse,
  PmBonusReport,
  PmCreditState,
  SetPmBonusRateBody,
  SetPmBonusRateReceipt,
  SetPmCreditBody,
  SetPmCreditReceipt,
} from "@truckbuddy/contract";
import { apiRequest } from "./api";

export const pmBonusKeys = {
  all: ["pm-bonus"] as const,
  credit: (workOrderId: string) => ["pm-bonus", "credit", workOrderId] as const,
  rates: ["pm-bonus", "rates"] as const,
  report: (scope: unknown) => ["pm-bonus", "report", scope] as const,
};

export function pmCreditPath(workOrderId: string): string {
  return `/work-orders/${workOrderId}/pm-credit`;
}

/** The credit card's read. `staleTime: 0`: a credit somebody else just set must show. */
export function usePmCredit(workOrderId: string, enabled = true) {
  return useQuery({
    queryKey: pmBonusKeys.credit(workOrderId),
    queryFn: () => apiRequest<PmCreditState>(pmCreditPath(workOrderId)),
    enabled,
    staleTime: 0,
  });
}

/** Record or correct one credit. Re-read afterwards: the server decides the version. */
export function useSetPmCredit(workOrderId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: SetPmCreditBody) =>
      apiRequest<{ data: SetPmCreditReceipt }>(pmCreditPath(workOrderId), { method: "PUT", body }),
    onSuccess: () => client.invalidateQueries({ queryKey: pmBonusKeys.all }),
    retry: false,
  });
}

export function usePmBonusRates(enabled = true) {
  return useQuery({
    queryKey: pmBonusKeys.rates,
    queryFn: () => apiRequest<PmBonusRatesResponse>("/pm-bonus/rates"),
    enabled,
    staleTime: 0,
  });
}

export function useSetPmBonusRate() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: SetPmBonusRateBody) =>
      apiRequest<{ data: SetPmBonusRateReceipt }>("/pm-bonus/rate", { method: "PUT", body }),
    onSuccess: () => client.invalidateQueries({ queryKey: pmBonusKeys.all }),
    retry: false,
  });
}

export interface PmBonusReportScope {
  timeframe: "all" | "30d" | "quarter" | "ytd";
  start?: string;
  end?: string;
  mechanicUserId?: string;
}

/** The Reports panel and its drill list: one read, so the two always agree. */
export function usePmBonusReport(scope: PmBonusReportScope, enabled = true) {
  return useQuery({
    queryKey: pmBonusKeys.report(scope),
    queryFn: () => apiRequest<Envelope<PmBonusReport>>("/reports/pm-bonuses", { query: scope }),
    enabled,
  });
}
